import { serverName } from '../live-ui/format'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get, type Profile, type StreamKey } from '../api'
import { DestinationEditor } from '../live-ui/DestinationEditor'
import { ObsSettings } from '../live-ui/ObsSettings'
import { SetupWizard } from '../live-ui/SetupWizard'
import { Banner } from '../live-ui/ui'
import { t } from '../i18n'
import { AddForm, Catalog } from '../live-ui/studio/AddOutput'
import { ControlBar } from '../live-ui/studio/ControlBar'
import { InputStrip } from '../live-ui/studio/InputStrip'
import { InputPair, VerticalPreview } from '../live-ui/studio/Pair'
import { YouTubeDualWizard } from '../live-ui/studio/YouTubeDual'
import { InputTracks, trackCount } from '../live-ui/studio/InputTracks'
import { multitrack, sendingOf } from '../live-ui/tracks'
import { buildTips, fixKind, isDropped, type StageState } from '../live-ui/studio/model'
import { ModeSwitch, OutputsList, PanelHead, type RightMode } from '../live-ui/studio/Right'
import { Chats, type ChatFilter } from '../live-ui/studio/Chats'
import { useChat } from '../live-ui/studio/useChat'
import { Stage } from '../live-ui/studio/Stage'
import { StudioBar } from '../live-ui/studio/StudioBar'
import { Tips, TipsRail } from '../live-ui/studio/Tips'
import { useMaxOutputs, usePlatformAccounts, useLiveScreen, useScreen } from '../live-ui/studio/useLiveScreen'
import type { Destination } from '../live-ui/types'
import { CaptionsPanel } from '../live-ui/CaptionsPanel'
import { MarkToast } from '../live-ui/studio/MarkToast'
import { Icon } from '../ui'

type RightView = { k: 'list' } | { k: 'add'; pick?: Profile } | { k: 'edit'; id: number }
type PhoneTab = 'outs' | 'chats' | 'tips'

/** OBS went away this long ago at most: the stage still says "OBS stopped sending". */
const DROPPED_FOR_MS = 5 * 60_000

/** Keys typed into a field are text, not shortcuts. */
function typing(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

/** A panel opened over the phone layout: a bottom sheet. */
function Sheet({ title, onClose, children, testId }: { title: string; onClose?: () => void; children: ReactNode; testId: string }) {
  return (
    <div className="fixed inset-0 z-30 flex items-end bg-bg/70" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} data-testid={testId} onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-lg border-t border-accent bg-panel shadow-pop">
        {onClose && <PanelHead title={title} onClose={onClose} />}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

/**
 * Live · Studio, layout S1 "In place" (docs/live-studio.md, docs/live-studio-handoff.md stage 1):
 * tips on the left (Input replaces them), the stage in the centre with the input line under it,
 * Outputs ⇄ Chats on the right (Add output and an output's settings replace the list), the
 * control bar at the bottom. ≥ 1024 px the page does not scroll, the columns do; 768–1023 the
 * tips start folded; < 768 the stage, the input line, tabs and bottom sheets.
 */
export function Live() {
  const L = useLiveScreen()
  const maxOutputs = useMaxOutputs()
  const { m, now, user, profiles } = L
  const screen = useScreen()
  const phone = screen === 'phone'
  const accounts = usePlatformAccounts()
  const [left, setLeft] = useState<'tips' | 'input'>('tips')
  const [tipsOpen, setTipsOpen] = useState(screen === 'desktop')
  const [right, setRight] = useState<RightView>({ k: 'list' })
  const [mode, setMode] = useState<RightMode>('outs')
  const [phoneTab, setPhoneTab] = useState<PhoneTab>('outs')
  const [phoneInput, setPhoneInput] = useState(false)
  // VERT-1: the YouTube Dual stream wizard over the Studio (mockup D)
  const [dual, setDual] = useState(false)
  const lastLive = useRef<number | null>(null)
  const [chatFilter, setChatFilter] = useState<ChatFilter>('all')
  // The chat stream is held only while this page is open and an output's chat is read (docs/chat.md).
  const chatReads = !!m?.dests.some((d) => d.chat?.access === 'reads')
  const chatsVisible = phone ? phoneTab === 'chats' : mode === 'chats' && right.k === 'list'
  const chat = useChat(chatReads, chatsVisible)
  // the stream key's voice track (E1): the Voice tag in the track list
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys') })

  // Tablet ⇄ desktop: tips folded on the narrower one, open on the wider one.
  useEffect(() => setTipsOpen(screen === 'desktop'), [screen])

  // O — outputs, C — chats; never while typing, never with a modifier.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e)) return
      const k = e.key.toLowerCase()
      if (k !== 'o' && k !== 'c') return
      if (phone) setPhoneTab(k === 'o' ? 'outs' : 'chats')
      else if (right.k === 'list') setMode(k === 'o' ? 'outs' : 'chats')
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [phone, right.k])

  if (!L.view || !m) {
    return <p className="px-4 py-10 text-sm text-muted md:px-8" data-testid="live-wait" data-conn={L.conn}>{L.conn === 'limited' ? t('studio.limited') : L.conn === 'reconnecting' ? t('studio.reconnecting') : t('studio.loading')}</p>
  }

  if (m.summary.live) lastLive.current = now
  const dropped = isDropped(m, lastLive.current !== null && now - lastLive.current < DROPPED_FOR_MS)
  const wizardOn = user?.setup === 'pending'
  const stage: StageState = wizardOn && !phone ? 'wizard' : m.source.unknown ? 'unknown' : m.summary.live ? 'live' : dropped ? 'dropped' : 'prelive'
  const tips = buildTips(m, profiles, { dropped, user })
  const urgent = tips.filter((x) => x.kind === 'fix' || x.kind === 'input').length
  const streamLive = m.summary.live
  const chatApi = m.dests.some((d) => d.chat)
  const chatsN = chatApi
    ? m.dests.filter((d) => d.enabled && d.chat?.access === 'reads').length
    : m.dests.filter((d) => d.enabled && (d.platform === 'youtube' || d.platform === 'twitch')).length
  const editingDest = right.k === 'edit' ? m.dests.find((d) => d.id === right.id) : undefined

  const openInput = () => (phone ? setPhoneInput((v) => !v) : setLeft((l) => (l === 'input' ? 'tips' : 'input')))
  const openEdit = (id: number) => {
    setMode('outs')
    setRight((r) => (r.k === 'edit' && r.id === id ? { k: 'list' } : { k: 'edit', id }))
  }
  const openTips = () => {
    if (phone) setPhoneTab('tips')
    else {
      setLeft('tips')
      setTipsOpen(true)
    }
  }
  const onFix = (d: Destination) => (fixKind(d) === 'settings' ? openEdit(d.id) : openTips())
  const toggle = (d: Destination) => void L.toggle(d.id, d.enabled, d.name)
  const closeRight = () => setRight({ k: 'list' })
  /** 💬 of an output row: Chats, filtered to that output. */
  const openChat = (d: Destination) => {
    setChatFilter(d.outputId)
    if (phone) setPhoneTab('chats')
    else {
      closeRight()
      setMode('chats')
    }
  }

  const wizard = wizardOn ? <SetupWizard view={L.view} profiles={profiles} dests={L.saved} /> : null

  const banners = (
    <>
      {L.connectBanner && (
        <div className="flex items-start gap-2" data-testid="connect-banner">
          <div className="flex-1"><Banner tone={L.connectBanner.tone}>{L.connectBanner.text}</Banner></div>
          <button type="button" onClick={L.closeConnectBanner} aria-label={t('common.dismiss')} className="sx-btn ghost sm icon"><Icon name="close" size={16} /></button>
        </div>
      )}
      {(m.nodeDown || m.source.unknown) && (
        <Banner tone="warn">
          <span data-testid="node-unknown-banner">{t('studio.nodeDown', { node: m.source.node ? ` ${serverName(m.source.node, false)}` : '' })}</span>
        </Banner>
      )}
      {L.err && <Banner tone="err">{L.err}</Banner>}
    </>
  )

  const row = editingDest ? L.saved?.find((x) => x.id === editingDest.id) : undefined
  const editor = editingDest ? (
    profiles && row ? (
      <DestinationEditor key={editingDest.id} dest={editingDest} saved={row} profiles={profiles} streamLive={streamLive} fill={L.fill} captionsAllowed={!!user?.features?.captions} translationAllowed={!!user?.features?.captions && !!user?.features?.caption_translation}
        tracks={m.input?.tracks}
        onCancel={closeRight}
        onSubmit={async (b) => { await L.update(editingDest.id, b); closeRight() }}
        onRemove={async () => { await L.remove(editingDest.id); closeRight() }} />
    ) : (
      <p className="text-xs text-muted">{t('studio.loadingSettings')}</p>
    )
  ) : null

  const addBody = right.k === 'add' ? (
    right.pick ? (
      <AddForm key={right.pick.id} profile={right.pick} live={streamLive} count={m.dests.length} max={maxOutputs}
        onCancel={() => setRight({ k: 'add' })}
        onCreate={async (b) => { await L.create(b); closeRight(); setMode('outs') }} />
    ) : (
      <Catalog profiles={profiles} onPick={(p) => setRight({ k: 'add', pick: p })} onDual={() => { setDual(true); setRight({ k: 'list' }) }} />
    )
  ) : null
  const addTitle = right.k === 'add' && right.pick ? t('add.titleDetails') : t('add.title')

  const outputs = (
    <OutputsList dests={m.dests} now={now} phone={phone} justSaved={L.justSaved} editing={editingDest?.id} profiles={profiles} max={maxOutputs}
      unread={chat.unread.byOutput} onChat={openChat}
      onToggle={toggle} onEdit={openEdit} onSaveKey={L.saveKey} onAdd={() => setRight({ k: 'add' })} />
  )
  const chats = (
    <Chats dests={m.dests} accounts={accounts.data} now={now} connecting={L.connecting} onConnect={(p) => void L.connect(p)}
      onOutputs={() => (phone ? setPhoneTab('outs') : setMode('outs'))} onFix={onFix}
      chat={chat} filter={chatFilter} onFilter={setChatFilter} phone={phone} />
  )
  // Captions of the stream (docs/captions-overlay.md §10.6): only for accounts with features.captions.
  const captions = user?.features?.captions && !wizardOn ? (
    <CaptionsPanel streamName={m.source.streamName} captions={L.view.inputs.find((i) => i.stream_name === m.source.streamName)?.captions ?? L.view.inputs[0]?.captions} streamLive={streamLive} tracks={m.input?.tracks} />
  ) : null
  // E1 tracks of the input (design system InputTracks): the Input panel and the phone Input sheet
  const tracks = m.input?.tracks
  const voiceKey = keys.data?.find((k) => k.stream_name === m.source.streamName)
  const inputTracks = (
    <InputTracks tracks={tracks} warnings={m.input?.track_warnings} sending={m.dests.map((d) => sendingOf(d.enabled, d.tracks))} voice={voiceKey?.voice_audio_track}
      limits={user?.limits} plan={user?.plan?.name} now={now} />
  )
  const inputTitle = multitrack(tracks) ? `${t('input.title')} · ${trackCount(tracks)}` : t('input.title')
  // VERT-1: the vertical input beside the stage (VT-01, VT-14) and the two-input summary under it (mockup A, G)
  const pair = m.vertical
  const showPair = !!pair && stage !== 'wizard' && stage !== 'unknown' && (stage === 'live' || pair.live || (stage === 'dropped' && pair.on))
  const inputLine = pair?.on && stage !== 'wizard'
    ? <InputPair m={m} pair={pair} phone={phone} open={phone ? phoneInput : left === 'input'} onOpen={openInput} />
    : <InputStrip m={m} phone={phone} open={phone ? phoneInput : left === 'input'} onOpen={openInput} />
  const stageRow = (st: ReactNode) => showPair && pair ? (
    // 16:9 and 9:16 of one height: columns 256 : 81 (16·16 : 9·9)
    <div data-testid="stage-pair" className="mx-auto grid w-full flex-none grid-cols-[minmax(0,256fr)_minmax(0,81fr)] items-start gap-3"
      style={phone ? undefined : { maxWidth: 'calc((100dvh - 400px) * 16 / 9 * 337 / 256 + 12px)' }}>
      {st}
      <VerticalPreview pair={pair} now={now} phone={phone} />
      {/* VT-13: the vertical input came before the main one */}
      {pair.live && !pair.mainLive && <p data-testid="vertical-ready" className="col-span-2 m-0 text-right text-xs text-muted">{t('vert.f.ready')}</p>}
    </div>
  ) : st
  const dualWizard = dual ? <YouTubeDualWizard streamName={m.source.streamName} phone={phone} onClose={() => setDual(false)} onDone={() => setMode('outs')} /> : null
  const tipsList = (only?: 'before') => (
    <Tips tips={tips} profiles={profiles} now={now} onSaveKey={L.saveKey} onSettings={openEdit} only={only}
      onVertical={() => (phone ? setPhoneInput(true) : setLeft('input'))} />
  )

  /* ── phone: stage, input line, Outputs · Chats · Tips, bottom sheets ── */
  if (phone) {
    const TABS: [PhoneTab, string][] = [['outs', `${t('right.outputs')} · ${m.dests.filter((d) => d.enabled).length}`], ['chats', t('right.chats')], ['tips', t('tips.title')]]
    return (
      <div data-testid="studio" data-screen="phone" className="flex min-h-full flex-col">
        <StudioBar m={m} now={now} tab="studio" dropped={dropped} phone />
        <div className="flex flex-1 flex-col gap-3 px-3 py-3">
          {banners}
          {stageRow(<Stage m={m} now={now} state={stage} phone quiet={phoneInput || wizardOn} onInput={openInput} />)}
          {inputLine}
          {captions}
          <div role="tablist" aria-label={t('right.label')} data-testid="phone-tabs" className="flex border-b border-line">
            {TABS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={phoneTab === k} data-testid={`phone-tab-${k}`} onClick={() => setPhoneTab(k)}
                className={`min-h-11 flex-1 px-2 text-[13px] ${phoneTab === k ? 'text-text shadow-[inset_0_-2px_0_var(--accent)]' : 'text-muted'}`}>
                {l}{k === 'tips' && urgent > 0 && <span className="ml-1 inline-flex items-center gap-1 align-[-1px] font-mono text-[11px] text-warn"><Icon name="state-warn" size={12} />{urgent}</span>}
                {k === 'chats' && chat.unread.total > 0 && <span data-testid="phone-chats-unread" className="num ml-1 font-mono text-[11px] text-accent">{chat.unread.total > 999 ? '999+' : chat.unread.total}</span>}
              </button>
            ))}
          </div>
          <div className="flex min-h-[200px] flex-col rounded-lg border border-line">
            {phoneTab === 'outs' ? outputs : phoneTab === 'chats' ? chats : <div className="p-3">{tipsList()}</div>}
          </div>
        </div>
        <ControlBar m={m} phone inputOpen={phoneInput} onInput={openInput} onAll={L.setAll} />
        <MarkToast eff={m.input?.watermark_effective} />
        {dualWizard}
        {wizardOn && <Sheet testId="sheet-wizard" title={t('wizard.title')}>{wizard}</Sheet>}
        {!wizardOn && phoneInput && (
          <Sheet testId="sheet-input" title={inputTitle} onClose={() => setPhoneInput(false)}>
            <div className="p-3">{multitrack(tracks) && <div className="mb-3">{inputTracks}</div>}<ObsSettings heading={false} streamName={m.source.streamName} protocol={m.source.protocol} onDone={() => setPhoneInput(false)} /></div>
          </Sheet>
        )}
        {!wizardOn && right.k === 'add' && <Sheet testId="sheet-add" title={addTitle} onClose={closeRight}><div className="p-3">{addBody}</div></Sheet>}
        {!wizardOn && editingDest && (
          <Sheet testId="sheet-edit" title={t('edit.title', { name: editingDest.name })} onClose={closeRight}><div className="p-3">{editor}</div></Sheet>
        )}
      </div>
    )
  }

  /* ── tablet and desktop: three columns, the page does not scroll ── */
  const leftWide = left === 'input' || wizardOn || tipsOpen
  const cols = `${leftWide ? '288px' : '52px'} minmax(0,1fr) ${screen === 'desktop' ? '340px' : '300px'}`
  return (
    <div data-testid="studio" data-screen={screen} className="flex h-full min-h-0 flex-col">
      <StudioBar m={m} now={now} tab="studio" dropped={dropped} phone={false} chats={mode === 'chats' && right.k === 'list'}
        onChats={() => { closeRight(); setMode((x) => (x === 'chats' ? 'outs' : 'chats')) }} />
      <div className="grid min-h-0 flex-1" style={{ gridTemplateColumns: cols }}>
        <aside data-testid="left-column" data-panel={wizardOn ? 'guide' : left === 'input' ? 'input' : tipsOpen ? 'tips' : 'rail'} aria-label={left === 'input' ? t('input.title') : t('tips.title')}
          className="flex min-h-0 flex-col border-r border-line">
          {left === 'input' && !wizardOn ? (
            <>
              <PanelHead testId="input-panel-head" title={t('input.title')} onClose={() => setLeft('tips')} />
              <div data-testid="input-panel" className="min-h-0 flex-1 overflow-y-auto p-3">
                {multitrack(tracks) && <div className="mb-3">{inputTracks}</div>}
                <p className="m-0 mb-3 text-[11px] text-muted">{t('input.applies')}</p>
                <ObsSettings heading={false} streamName={m.source.streamName} protocol={m.source.protocol} onDone={() => setLeft('tips')} />
              </div>
            </>
          ) : wizardOn ? (
            <>
              <PanelHead title={t('wizard.title')} />
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
                <span className="text-[13px] text-text-soft">{t('wizard.side')}</span>
                {tipsList('before')}
              </div>
            </>
          ) : (
            <>
              <div className="flex min-h-11 flex-none items-center gap-2 border-b border-line px-2">
                {tipsOpen && <span className="sx-label pl-1">{t('tips.title').toUpperCase()}</span>}
                {tipsOpen && urgent > 0 && <span className="inline-flex items-center gap-1 font-mono text-[11px] text-warn"><Icon name="state-warn" size={12} />{urgent}</span>}
                <button type="button" data-testid="tips-fold" onClick={() => setTipsOpen((v) => !v)} aria-expanded={tipsOpen} aria-label={tipsOpen ? t('tips.fold') : t('tips.open')}
                  className="sx-btn ghost sm icon ml-auto"><Icon name="collapse-left" size={16} className={tipsOpen ? '' : 'rotate-180'} /></button>
              </div>
              {tipsOpen ? <div className="min-h-0 flex-1 overflow-y-auto p-3">{tipsList()}</div> : <TipsRail tips={tips} onOpen={() => setTipsOpen(true)} />}
            </>
          )}
        </aside>

        <main data-testid="center-column" className="flex min-h-0 flex-col gap-3 overflow-y-auto px-4 py-3">
          {banners}
          {stageRow(<Stage m={m} now={now} state={stage} wizard={wizard} quiet={left === 'input'} onInput={openInput} onBack={() => setLeft('tips')} />)}
          {stage !== 'wizard' && inputLine}
          {stage !== 'wizard' && captions}
        </main>

        <aside data-testid="right-column" data-panel={right.k === 'list' ? mode : right.k} aria-label={t('right.label')} className="flex min-h-0 flex-col border-l border-line">
          {right.k === 'add' ? (
            <>
              <PanelHead testId="add-head" title={addTitle} onBack={right.pick ? () => setRight({ k: 'add' }) : undefined} onClose={closeRight} />
              <div className="min-h-0 flex-1 overflow-y-auto p-3">{addBody}</div>
            </>
          ) : editingDest ? (
            <>
              <PanelHead testId="edit-head" title={t('edit.title', { name: editingDest.name })} onBack={closeRight} />
              <div className="min-h-0 flex-1 overflow-y-auto p-3">{editor}</div>
            </>
          ) : (
            <>
              <ModeSwitch mode={mode} onMode={setMode} dests={m.dests} chats={chatsN} chatApi={chatApi} unread={chat.unread.total} />
              {mode === 'outs' ? outputs : chats}
            </>
          )}
        </aside>
      </div>
      <ControlBar m={m} inputOpen={left === 'input'} onInput={openInput} onAll={L.setAll} />
      <MarkToast eff={m.input?.watermark_effective} />
      {dualWizard}
    </div>
  )
}
